import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { ChipStatus, DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { FeatureDTO, FeatureFilterDTO, TestScenarioDTO, TestScenarioFilterDTO, UserDTO, ValueListDTO, ValueListFilterDTO } from '../../../shared/interfaces';
import { TestComponentDTO } from '../../../shared/interfaces/testingSphere/TestComponentDTO';
import { dateDiff, formatDateTime } from '../../../shared/utils';

interface ITestComponentMaintenanceProps {
    data: any;
    parentId?: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: true) => void;
}

export const TestComponentMaintenance: React.FC<ITestComponentMaintenanceProps> = ({ data, parentId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [executionStatusList, setExecutionStatusList] = useState<ValueListDTO[]>([]);
    const [testScenarioList, setTestScenarioList] = useState<TestScenarioDTO[]>([]);
    const [featureList, setFeatureList] = useState<FeatureDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [testComponentDTO, setTestComponentDTO] = useState<TestComponentDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setTestComponentDTO({
            ...testComponentDTO,
            id: '',
            name: '',
            orderExecution: 0,
            cycle: 0,
            description: '',
            testScenarioId: parentId ?? ''
        } as TestComponentDTO);

        if(isOpenMaintenance){
            handleListGetAll(ListId.testingsphere.testpackage.executionstatus, setExecutionStatusList);
            handleFeatureGetAllAsync();
            handleTestScenarioGetAllAsync();
            handleUserGetAll();
            handleGetByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleUserGetAll = useCallback(async () => {
        const filterUser = {
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('authsphere.user', filterUser, setLoading, setUserList, showMessage);
    }, []);

    const handleStatusExecutionGetAllAsync = useCallback(async () => {
        const valueListFilterDTO: ValueListFilterDTO = {
            value: '',
            listId: ListId.executionStatus,
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setExecutionStatusList, showMessage);
    }, []);

    const handleFeatureGetAllAsync = useCallback(async () => {
        const featureFilterDTO: FeatureFilterDTO = {
            name: '',
            applicationId: '',
        };
        GenericService.GetAllAsync1('appsphere.feature', featureFilterDTO, setLoading, setFeatureList, showMessage);
    }, []);

    const handleTestScenarioGetAllAsync = useCallback(async () => {
        const testScenarioFilterDTO: TestScenarioFilterDTO = {
            name: '',
            testPackageId: '',
        };
        GenericService.GetAllAsync1('testingsphere.testScenario', testScenarioFilterDTO, setLoading, setTestScenarioList, showMessage);
    }, []);

    const handleGetByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('testingsphere.testcomponent', data.id, setLoading, setTestComponentDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = testComponentDTO as TestComponentDTO;
        if (testComponentDTO?.id === '' || testComponentDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.PostGenericAsync1('testcomponent', 'AddSequenceTestComponentAsync', dto, setActionExecuted, setLoading, showMessage);
        }
        else {
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('testingsphere.testcomponent', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [testComponentDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];
    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='TEST COMPONENT DETAILS'
                icon='settings_ethernet_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={10}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={testComponentDTO?.name}
                                setValue={setTestComponentDTO}
                                obj={testComponentDTO}
                                required={true}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='orderExecution'
                                label='Order Execution'
                                value={testComponentDTO?.orderExecution}
                                setValue={setTestComponentDTO}
                                obj={testComponentDTO}
                                required={true}
                                type={'number'}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='featureId'
                                label='Feature Name'
                                value={testComponentDTO?.featureId}
                                setValue={setTestComponentDTO}
                                obj={testComponentDTO}
                                valueList={featureList}
                                disabled={parentId ?? testComponentDTO?.id ? true : false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='testScenarioId'
                                label='TestScenario Name'
                                value={testComponentDTO?.testScenarioId}
                                setValue={setTestComponentDTO}
                                obj={testComponentDTO}
                                valueList={testScenarioList}
                                disabled={parentId ?? testComponentDTO?.id ? true : false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={testComponentDTO?.description}
                                setValue={setTestComponentDTO}
                                obj={testComponentDTO}
                                required={true}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextFieldReadOnly
                                id={'executionStatusId'}
                                label='Status Execution'
                                value={(<ChipStatus label='Execution Status' value={String(executionStatusList.find(x => x.id === testComponentDTO?.executionStatusId)?.value || 'No Run')} />)}
                                hasDivider={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'cycle'}
                                label='Cycle'
                                value={(<ChipStatus label='Cycle' value={String(testComponentDTO?.cycle)} />)}
                                hasDivider={false}
                            />
                        </Grid>
                        {testComponentDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'startExecutionDateTime'}
                                        label='Start Execution'
                                        value={formatDateTime(String(testComponentDTO.startExecutionDateTime))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'endExecutionDateTime'}
                                        label='End Execution'
                                        value={formatDateTime(String(testComponentDTO.endExecutionDateTime))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'executionTime'}
                                        label='Execution Time'
                                        value={testComponentDTO.startExecutionDateTime ? dateDiff(String(testComponentDTO.startExecutionDateTime), String(testComponentDTO.endExecutionDateTime)) : undefined}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextFieldReadOnly
                                        id={'featureId'}
                                        label='Feature'
                                        value={String(featureList.find(x => x.id === testComponentDTO?.featureId)?.name)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextFieldReadOnly
                                        id={'testScenarioId'}
                                        label='TestScenario'
                                        value={String(testScenarioList.find(x => x.id === testComponentDTO?.testScenarioId)?.name)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={testComponentDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === testComponentDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(testComponentDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === testComponentDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(testComponentDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};